
import get from 'lodash/get'
import { reactive, computed } from '@nuxtjs/composition-api/dist/index.js'

export default {
  provide() {
    return {
      wizard: reactive({
        mode: computed(() => this.mode),
        storeModule: computed(() => this.storeModule),
        baseRoute: computed(() => this.baseRoute),
        steps: computed(() => this.mappedSteps),
        currentStep: computed(() => this.currentStep),
        questions: computed(() => this.mappedQuestions),
        currentQuestionIndex: computed(() => this.currentQuestionIndex),
        currentQuestion: computed(() => this.currentQuestion),
        isFirstQuestion: computed(() => this.isFirstQuestion),
        isLastQuestion: computed(() => this.isLastQuestion),
        goToStep: this.goToStep,
        goToQuestion: this.goToQuestion,
        prevQuestion: computed(() => this.prevQuestion),
        prevQuestionRoute: computed(() => this.prevQuestionRoute),
        goPrev: this.goPrev,
        nextQuestion: computed(() => this.nextQuestion),
        nextQuestionRoute: computed(() => this.nextQuestionRoute),
        goNext: this.goNext,

        error: computed(() => this.error),
        setError: this.setError,
        resetError: this.resetError,
      }),
    }
  },

  props: {
    mode: {
      type: String, // Possible values: 'components', 'routes'
      default: 'components',
    },
    storeModule: {
      type: String, // Path of the corresponding store module
      required: true,
    },
  },

  data() {
    const data = {
      error: null,
    }

    if (this.mode === 'routes') {
      return {
        ...data,
      }
    }

    return {
      ...data,
      actualCurrentQuestionIndex: 0,
    }
  },

  computed: {
    storeState() {
      return get(this.$store.state, this.storeModule.replace(/\//g, '.'))
    },

    steps() {
      const filteredSteps =
        this.$store.getters[this.storeModule + '/filteredSteps']

      if (Array.isArray(filteredSteps)) {
        return filteredSteps
      }

      return this.storeState.steps
    },

    questions() {
      return this.steps.flatMap((step, stepIndex) => {
        const actualQuestions = step.questions.filter(
          (question) => !question.ignoreInQuestionCount
        )
        return step.questions.map((question, questionIndex) => {
          return {
            ...question,

            route:
              this.mode === 'routes'
                ? this.$router.match(
                    question.route
                      ? this.localeRoute({
                          ...question.route,
                          params: {
                            id: ':id',
                          },
                        })
                      : '/'
                  )
                : undefined,

            stepIndex,
            step,
            questionIndexWithinStep: actualQuestions.findIndex(
              (q) => q.id === question.id
            ),
            isFirstOfStep: questionIndex === 0,
            isLastOfStep: questionIndex === step.questions.length - 1,
          }
        })
      })
    },

    mappedQuestions() {
      return this.questions.map((question, i, questions) => ({
        ...question,

        isFirst: i === 0,
        isLast: i === questions.length - 1,
        state:
          this.currentQuestionIndex > i
            ? 'prev'
            : this.currentQuestionIndex < i
            ? 'next'
            : 'active',
      }))
    },

    computedCurrentQuestionIndex() {
      if (this.mode === 'routes') {
        return this.questions.findIndex((question) => {
          return question.route.name === this.$route.name
        })
      }

      return this.actualCurrentQuestionIndex
    },

    // This question index is safe. It will not be an invalid question index no matter what.
    currentQuestionIndex() {
      return Math.min(
        Math.max(this.computedCurrentQuestionIndex, 0),
        this.questions.length - 1
      )
    },

    currentQuestion() {
      return this.mappedQuestions[this.currentQuestionIndex]
    },

    isFirstQuestion() {
      return this.currentQuestionIndex === 0
    },

    isLastQuestion() {
      return this.currentQuestionIndex === this.questions.length - 1
    },

    prevQuestionIndex() {
      return this.isFirstQuestion ? null : this.currentQuestionIndex - 1
    },

    prevQuestion() {
      return this.prevQuestionIndex === null
        ? null
        : this.mappedQuestions[this.prevQuestionIndex]
    },

    prevQuestionRoute() {
      if (this.mode === 'routes') {
        return this.prevQuestion?.route
      }

      return null
    },

    nextQuestionIndex() {
      return this.isLastQuestion ? null : this.currentQuestionIndex + 1
    },

    nextQuestion() {
      return this.nextQuestionIndex === null
        ? null
        : this.mappedQuestions[this.nextQuestionIndex]
    },

    nextQuestionRoute() {
      if (this.mode === 'routes') {
        return this.nextQuestion?.route
      }

      return null
    },

    mappedSteps() {
      return this.steps
        .map((step, i) => {
          const questions = this.mappedQuestions.filter(
            (question) => question.stepIndex === i
          )

          return {
            ...step,

            questions,
            totalNumberOfQuestions: questions.filter(
              (question) => !question.ignoreInQuestionCount
            ).length,
            completedNumberOfQuestions: questions.filter(
              (question) =>
                !question.ignoreInQuestionCount && question.state === 'prev'
            ).length,

            isFirst: i === 0,
            isLast: i === this.steps.length - 1,
            state:
              this.currentStepIndex > i
                ? 'prev'
                : this.currentStepIndex < i
                ? 'next'
                : 'active',
          }
        })
        .map((step, i, steps) => ({
          ...step,

          // Editable if the step is the preceding step or if the step is one of the following steps and has been touched before.
          isEditable:
            (i === this.currentStepIndex - 1 &&
              !steps[this.currentStepIndex].isCompleted) ||
            (this.currentStepIndex < i && step.isTouched),
        }))
    },

    currentStepIndex() {
      return this.currentQuestion.stepIndex
    },

    currentStep() {
      return this.mappedSteps[this.currentStepIndex]
    },
  },

  watch: {
    computedCurrentQuestionIndex() {
      this.ensureCurrentQuestionIsValid()

      this.resetError()

      this.$scrollTo?.(this.$el)
    },
  },

  created() {
    if (!this.$store.hasModule(this.storeModule.split('/'))) {
      throw new Error(
        'Wizard has to receive an existing and valid store module.'
      )
    }

    this.ensureCurrentQuestionIsValid()
  },

  methods: {
    goToStep(step, { params, ...routeOptions } = {}, method = 'push') {
      const question = step.questions.find((question) => !question.isLocked)

      if (question) {
        this.goToQuestion(question, { params, ...routeOptions }, method)
      }
    },

    goToQuestion(question, { params, ...routeOptions } = {}, method = 'push') {
      if (this.mode === 'routes') {
        this.$router[method]({
          ...question.route,
          params: { ...this.$route.params, ...params },
          ...routeOptions,
        })
      } else {
        this.actualCurrentQuestionIndex = this.mappedQuestions.findIndex(
          (q) => q.id === question.id
        )
      }
    },

    goPrev({ params, ...routeOptions } = {}, method = 'push') {
      if (this.mode === 'routes') {
        if (this.prevQuestionRoute) {
          this.$router[method]({
            ...this.prevQuestionRoute,
            params: { ...this.$route.params, ...params },
            ...routeOptions,
          })
        }
      } else if (typeof this.prevQuestionIndex === 'number') {
        this.actualCurrentQuestionIndex = this.prevQuestionIndex
      }
    },

    goNext({ params, ...routeOptions } = {}, method = 'push') {
      if (this.mode === 'routes') {
        if (this.nextQuestionRoute) {
          this.$router[method]({
            ...this.nextQuestionRoute,
            params: { ...this.$route.params, ...params },
            ...routeOptions,
          })
        }
      } else if (typeof this.nextQuestionIndex === 'number') {
        this.actualCurrentQuestionIndex = this.nextQuestionIndex
      }
    },

    ensureCurrentQuestionIsValid() {
      let firstIncompleteQuestionIndex = this.mappedQuestions.findIndex(
        (question) => !question.isCompleted
      )
      firstIncompleteQuestionIndex =
        firstIncompleteQuestionIndex > -1
          ? firstIncompleteQuestionIndex
          : this.mappedQuestions.length - 1

      const firstIncompleteQuestion =
        this.mappedQuestions[firstIncompleteQuestionIndex]

      if (
        // If we are on an invalid question (eg. a question that should be skipped)
        this.computedCurrentQuestionIndex < 0 ||
        // If we are on a question that should not be available yet (the previous question is not completed)
        (firstIncompleteQuestion?.route &&
          this.computedCurrentQuestionIndex > firstIncompleteQuestionIndex)
      ) {
        // Go to the first question that is not completed
        this.goToQuestion(firstIncompleteQuestion, {}, 'replace')
      }
    },

    setError(error) {
      this.error = error
    },

    resetError() {
      this.error = null
    },
  },

  render() {
    return this.$scopedSlots.default(this._provided.wizard)
  },
}
